import { Edge, Node } from '@xyflow/react'
import ELK from 'elkjs/lib/elk.bundled.js'
import type { ElkNode } from 'elkjs/lib/elk-api'
import { FlowElementsType, IEdgeType } from '../types/wizard-flow'

const mapLayoutOptionsToNodeType: Record<string, Record<string, string>> = {
    action: {
        'elk.direction': 'DOWN',
        'elk.layered.crossingMinimization.semiInteractive': 'true',
        'elk.spacing.nodeNode': '80',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
    },
    trigger: {
        'elk.direction': 'DOWN',
        'elk.layered.crossingMinimization.semiInteractive': 'true',
        'elk.spacing.nodeNode': '80',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
    },
    placeholderNode: {
        'elk.direction': 'DOWN',
        'elk.layered.crossingMinimization.semiInteractive': 'true',
        'elk.spacing.nodeNode': '80',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
    },
    placeholderCondition: {
        'elk.direction': 'DOWN',
        'elk.layered.crossingMinimization.semiInteractive': 'true',
        'elk.spacing.nodeNode': '80',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
    },
    loop: {
        'elk.direction': 'DOWN',
        'elk.algorithm': 'layered',
        'elk.spacing.nodeNode': '80',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
        'elk.padding': '[top=40,left=30,bottom=40,right=30]',
        'elk.contentAlignment': 'H_CENTER',
        'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
        'elk.layered.nodePlacement.bk.fixedAlignment': 'CENTER',
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.layered.spacing.edgeNodeBetweenLayers': '30',
        'elk.layered.mergeEdges': 'true',
        'elk.layered.unnecessaryBendpoints': 'true',
        'elk.layered.cycleBreaking.strategy': 'INTERACTIVE',
        'elk.layered.layering.strategy': 'NETWORK_SIMPLEX',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        'elk.layered.nodePlacement.favorStraightEdges': 'true',
        'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
    },
    condition_group: {
        'elk.direction': 'DOWN',
        'elk.algorithm': 'layered',
        'elk.spacing.nodeNode': '20',
        'elk.layered.spacing.nodeNodeBetweenLayers': '80',
        'elk.padding': '[top=40,left=30,bottom=40,right=30]',
        'elk.contentAlignment': 'H_CENTER',
        'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
        'elk.layered.nodePlacement.bk.fixedAlignment': 'CENTER',
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.layered.spacing.edgeNodeBetweenLayers': '30',
        'elk.layered.mergeEdges': 'false',
        'elk.layered.unnecessaryBendpoints': 'true',
        'elk.layered.cycleBreaking.strategy': 'INTERACTIVE',
        'elk.layered.layering.strategy': 'NETWORK_SIMPLEX',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        'elk.portAlignment.default': 'CENTER',
        'elk.layered.nodePlacement.favorStraightEdges': 'true',
    },
    condition: {
        'elk.direction': 'DOWN',
        'elk.algorithm': 'layered',
        'elk.hierarchyHandling': 'SEPARATE_CHILDREN',
        'elk.edgeRouting': 'ORTHOGONAL',
        'elk.spacing.nodeNode': '20',
        'elk.layered.spacing.nodeNodeBetweenLayers': '20',
        'elk.contentAlignment': 'H_CENTER',
        'elk.portAlignment.default': 'CENTER',
        'elk.portConstraints': 'FIXED_ORDER',
    },
}

const doElkLayout = async (nodes: Node[], edges: Edge[]): Promise<ElkNode> => {
    const layout: ElkNode = {
        id: 'root',
        layoutOptions: {
            'elk.algorithm': 'elk.layered',
            'elk.direction': 'DOWN',
            'elk.spacing.nodeNode': '50',
            'elk.layered.spacing.nodeNodeBetweenLayers': '80',
            'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
            'elk.edgeRouting': 'ORTHOGONAL',
            'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
            'elk.layered.nodePlacement.favorStraightEdges': 'true',
            'elk.contentAlignment': 'H_CENTER',
            'elk.nodeSize.constraints': 'FIXED_SIZE',
            'elk.portAlignment.default': 'CENTER',
            'elk.separateConnectedComponents': 'false',
            'elk.spacing.componentComponent': '0',
            'elk.layered.spacing.edgeNodeBetweenLayers': '30',
            'elk.layered.spacing.edgeEdgeBetweenLayers': '30',
            'elk.layered.mergeEdges': 'false',
            'elk.layered.unnecessaryBendpoints': 'true',
            'elk.layered.nodePlacement.bk.fixedAlignment': 'CENTER',
            'elk.layered.cycleBreaking.strategy': 'DEPTH_FIRST',
            'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
            'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
            'elk.spacing.individualOverride': '[source=condition,target=action,spacing=80]',
        },
        children: nodes
            .filter(node => !node.parentId)
            .map(node => ({
                id: node.id,
                // @ts-ignore
                width: node.width,
                height: node.height,
                // @ts-ignore
                type: node.type,
                edges: [],
                // @ts-ignore
                position: { x: node.position.x, y: node.position.y },
                data: { label: node.data?.label, ...node.data },
                // @ts-ignore
                selected: node.selected,
                // @ts-ignore
                zIndex: node.zIndex,
                style: node.style,
                layoutOptions: mapLayoutOptionsToNodeType[node.type || 'action'],
                children: nodes
                    .filter(n => n.parentId === node.id)
                    .map(n => ({
                        id: n.id,
                        // @ts-ignore
                        width: n.width,
                        height: n.height,
                        // @ts-ignore
                        type: n.type,
                        edges: [],
                        // @ts-ignore
                        position: { x: n.position.x, y: n.position.y },
                        data: { label: n.data?.label, ...n.data },
                        // @ts-ignore
                        selected: n.selected,
                        // @ts-ignore
                        zIndex: n.zIndex,
                        style: n.style,
                    })),
            })),
        edges: edges.map(edge => ({
            id: edge.id,
            sources: [edge.source],
            targets: [edge.target],
        })),
    }

    try {
        const elk = new ELK()
        const elkLayout = await elk.layout(layout)
        return Promise.resolve(elkLayout)
    } catch (error) {
        console.error('doElkLayout error:', error)
        return Promise.reject(error)
    }
}

export const doLayout = async (nodes: Node[], edges: Edge[]): Promise<FlowElementsType> => {
    const elkLayout = await doElkLayout(nodes, edges)

    const flowElements: FlowElementsType = {
        nodes: [],
        edges: [],
    }

    elkLayout.children?.forEach(node => {
        flowElements.nodes.push({
            id: node.id!,
            // @ts-ignore
            type: node.type,
            position: { x: node.x!, y: node.y! },
            // @ts-ignore
            data: node.data,
            // @ts-ignore
            zIndex: node.zIndex || node.data?.zIndex,
            // @ts-ignore
            selected: node.selected,
            // @ts-ignore
            style: node.style,
            width: node.width,
            height: node.height,
        })

        if (node.children) {
            node.children.forEach(child => {
                flowElements.nodes.push({
                    id: child.id!,
                    // @ts-ignore
                    type: child.type,
                    position: { x: child.x!, y: child.y! },
                    // @ts-ignore
                    data: child.data,
                    parentId: node.id,
                    // @ts-ignore
                    zIndex: node.zIndex || node.data?.zIndex,
                    // @ts-ignore
                    selected: node.selected,
                    // @ts-ignore
                    style: child.style,
                    width: child.width,
                    height: child.height,
                })
            })
        }
    })

    elkLayout.edges?.forEach(edge => {
        flowElements.edges.push({
            id: edge.id!,
            source: edge.sources![0],
            target: edge.targets![0],
            style: { stroke: '#63316B', strokeWidth: 1, strokeOpacity: 1 },
            type: IEdgeType.CUSTOM_EDGE,
        })
    })

    return Promise.resolve(flowElements)
}
